if (!customElements.get('variant-selects')) {

  /**
   *  @class
   *  @function VariantSelects
   */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.sticky = this.dataset.sticky;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.isDisabledFeature = this.dataset.isDisabled;
      this.preventScroll = this.dataset.preventScroll === 'true';
      this.addEventListener('change', this.onVariantChange);
      this.other = Array.from(document.querySelectorAll('variant-selects')).filter((selector) => {
        return selector != this;
      });

      this.productWrapper = this.closest('.thb-product-detail');
      this.productSlider = this.productWrapper?.querySelector('.product-images');
      this.hideVariants = this.dataset.hideVariants === 'true';
    }

    connectedCallback() {
      // Cache button references (per review: avoid repeated DOM queries)
      this.submitButtons = document.querySelectorAll('.single-add-to-cart-button');

      this.updateOptions();
      this.updateMasterId();
      this.setDisabled();
      this.setImageSet();
      this.initDeselection();
      if (this.preventScroll) this.preventFocusScroll();

      // Sync button state with actual selection state on page load
      // Fixes: Liquid pre-renders based on first variant, but no variant may be selected
      if (!this.currentVariant) {
        this.clearVariantInput();
        if (this.allOptionsSelected) {
          // All options selected but no matching variant = sold out/unavailable
          this.setButtonSoldOut();
        } else {
          // No size selected yet = show "Select options"
          this.setButtonChoose();
        }
      }
    }

    /**
     * Enable radio button deselection for required fields (Size option)
     * Allows users to click an already-selected option to deselect it
     */
    initDeselection() {
      // Only apply to fieldsets marked as required (Size option)
      const requiredFieldsets = this.querySelectorAll('fieldset[data-required="true"]');

      requiredFieldsets.forEach(fieldset => {
        // Track which radio was checked BEFORE any interaction
        let previouslyCheckedRadio = null;

        // Capture state at fieldset level (works for both label and input clicks)
        fieldset.addEventListener('mousedown', () => {
          previouslyCheckedRadio = fieldset.querySelector('input[type="radio"]:checked');
        });

        // Also handle touch devices
        fieldset.addEventListener('touchstart', () => {
          previouslyCheckedRadio = fieldset.querySelector('input[type="radio"]:checked');
        }, { passive: true });

        // Handle click - check if same radio was clicked
        fieldset.addEventListener('click', (e) => {
          // Find which radio was clicked (either directly or via label)
          let clickedRadio = e.target.closest('input[type="radio"]');
          if (!clickedRadio) {
            const label = e.target.closest('label');
            if (label?.htmlFor) {
              clickedRadio = document.getElementById(label.htmlFor);
            }
          }

          if (!clickedRadio) return;

          // If clicking the same radio that was already checked, deselect it
          // Use setTimeout to run AFTER browser's default click behavior
          if (clickedRadio === previouslyCheckedRadio) {
            const radioToDeselect = clickedRadio;
            setTimeout(() => {
              radioToDeselect.checked = false;
              this.handleDeselection(radioToDeselect, fieldset);
            }, 0);
          }

          previouslyCheckedRadio = null;
        });

        // Handle keyboard (Space key) on individual radios
        const radios = fieldset.querySelectorAll('input[type="radio"]');
        radios.forEach(radio => {
          radio.addEventListener('keydown', (e) => {
            if (e.key === ' ' && radio.checked) {
              e.preventDefault();
              this.handleDeselection(radio, fieldset);
            }
          });
        });
      });
    }

    /**
     * Prevent browser focus-scroll when clicking radio labels.
     * Captures scrollY on mousedown (before browser scrolls to hidden radio).
     */
    preventFocusScroll() {
      this.addEventListener('mousedown', () => {
        this._savedScrollY = window.scrollY;
      });
    }

    /**
     * Handle the deselection of a radio button
     */
    handleDeselection(radio, fieldset) {
      radio.checked = false;

      // Trigger change event to update variant
      radio.dispatchEvent(new Event('change', { bubbles: true }));

      // Update label to show "Select a size"
      const label = fieldset.querySelector('.form__label__value');
      if (label) {
        label.textContent = window.theme?.variantStrings?.selectSize || 'Select a size';
      }

      // Announce to screen readers
      this.announceDeselection();
    }

    /**
     * Announce deselection to screen readers
     */
    announceDeselection() {
      let announcer = document.getElementById('variant-announcer');
      if (!announcer) {
        announcer = document.createElement('div');
        announcer.id = 'variant-announcer';
        announcer.setAttribute('aria-live', 'polite');
        announcer.setAttribute('aria-atomic', 'true');
        announcer.className = 'visually-hidden';
        document.body.appendChild(announcer);
      }

      // Clear and re-announce for screen reader to pick up
      announcer.textContent = '';
      setTimeout(() => {
        announcer.textContent = window.theme?.variantStrings?.sizeDeselected || 'Size selection cleared';
      }, 100);
    }

    onVariantChange() {
      // Cancel any pending fetch to prevent race conditions
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      // Show updating state while processing
      this.setButtonUpdating();

      // Synchronous updates
      this.updateOptions();
      this.updateMasterId();
      this.updatePickupAvailability();
      this.removeErrorMessage();
      this.updateVariantText();
      this.setDisabled();

      if (!this.currentVariant) {
        // Clear the variant input so form validation can catch it
        this.clearVariantInput();

        // Set final button state ONCE (no fetch needed)
        if (this.allOptionsSelected) {
          // All options selected but no matching variant = sold out
          this.setButtonSoldOut();
        } else {
          // No size selected = show "Select options"
          this.setButtonChoose();
        }
      } else {
        // Valid variant selected - update inputs and media
        this.updateVariantInput();
        this.updateMedia();
        if (this.updateUrl) this.updateURL();

        // Set button state based on local data (no async flickering)
        // renderProductInfo updates price display only
        if (this.currentVariant.available) {
          this.setButtonReady();
        } else {
          this.setButtonSoldOut();
        }

        // Fetch updated price/inventory info (doesn't change button state)
        this.renderProductInfo();
      }

      this.updateOther();
      dispatchCustomEvent('product:variant-change', {
        variant: this.currentVariant,
        sectionId: this.dataset.section
      });

      // Restore scroll position after variant change (prevents page jump)
      if (this.preventScroll && this._savedScrollY != null) {
        const y = this._savedScrollY;
        this._savedScrollY = null;
        const restore = () => window.scrollTo({ top: y, behavior: 'instant' });
        restore();
        requestAnimationFrame(() => { restore(); requestAnimationFrame(restore); });
      }
    }

    updateOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = [];
      this.allOptionsSelected = true;

      this.fieldsets.forEach((fieldset, i) => {
        if (fieldset.querySelector('select')) {
          const selectValue = fieldset.querySelector('select').value;
          this.options.push(selectValue || null);
          if (!selectValue) this.allOptionsSelected = false;
        } else if (fieldset.querySelectorAll('input').length) {
          const checkedInput = fieldset.querySelector('input:checked');
          if (checkedInput) {
            this.options.push(checkedInput.value);
            // Clear error styling when option is selected
            fieldset.classList.remove('has-error');
            const errorMsg = fieldset.querySelector('.variant-error');
            if (errorMsg) errorMsg.remove();
          } else {
            this.options.push(null);
            this.allOptionsSelected = false;
          }
        }
      });
    }
    updateVariantText() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((item, i) => {
        let label = item.querySelector('.form__label__value');
        if (label) {
          // Show "Select a size" for required fieldsets with no selection
          if (this.options[i] === null && item.hasAttribute('data-required')) {
            label.textContent = window.theme?.variantStrings?.selectSize || 'Select a size';
          } else if (this.options[i] !== null) {
            label.innerHTML = this.options[i];
          }
        }
      });
    }
    updateMasterId() {
      // If not all options are selected, don't try to find a variant
      if (!this.allOptionsSelected) {
        this.currentVariant = null;
        return;
      }

      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    updateOther() {
      if (this.dataset.updateUrl === 'false') {
        return;
      }
      if (this.other.length) {
        let fieldsets = this.other[0].querySelectorAll('fieldset'),
          fieldsets_array = Array.from(fieldsets);
        this.options.forEach((option, i) => {
          if (option === null) {
            // Sync deselection: clear the other form's selection too
            if (fieldsets_array[i].querySelector('select')) {
              fieldsets_array[i].querySelector('select').value = '';
            } else if (fieldsets_array[i].querySelectorAll('input').length) {
              fieldsets_array[i].querySelectorAll('input').forEach(input => {
                input.checked = false;
              });
            }
          } else {
            // Sync selection
            if (fieldsets_array[i].querySelector('select')) {
              fieldsets_array[i].querySelector(`select`).value = option;
            } else if (fieldsets_array[i].querySelectorAll('input').length) {
              const targetInput = fieldsets_array[i].querySelector(`input[value='${option}']`);
              if (targetInput) targetInput.checked = true;
            }
          }
        });
        this.other[0].updateOptions();
        this.other[0].updateMasterId();
        this.other[0].updateVariantText();
        this.other[0].setDisabled();
      }
    }
    updateMedia() {
      if (!this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;
      if (!this.productSlider) return;

      let thumbnails = this.productWrapper.querySelector('.product-thumbnail-container');

      this.setActiveMedia(`#Slide-${this.dataset.section}-${this.currentVariant.featured_media.id}`, `#Thumb-${this.dataset.section}-${this.currentVariant.featured_media.id}`, this.productSlider, thumbnails);
    }
    setActiveMedia(mediaId, thumbId, productSlider, thumbnails) {
      let flkty = Flickity.data(productSlider),
        activeMedia = productSlider.querySelector(mediaId);
      if (flkty && this.hideVariants) {
        if (productSlider.querySelector('.product-images__slide.is-initial-selected')) {
          productSlider.querySelector('.product-images__slide.is-initial-selected').classList.remove('is-initial-selected');
        }
        [].forEach.call(productSlider.querySelectorAll('.product-images__slide-item--variant'), function (el) {
          el.classList.remove('is-active');
        });
        if (this.thumbnails) {
          if (this.thumbnails.querySelector('.product-thumbnail.is-initial-selected')) {
            this.thumbnails.querySelector('.product-thumbnail.is-initial-selected').classList.remove('is-initial-selected');
          }
          [].forEach.call(this.thumbnails.querySelectorAll('.product-images__slide-item--variant'), function (el) {
            el.classList.remove('is-active');
          });
        }

        if (activeMedia) {
          activeMedia.classList.add('is-active');
          activeMedia.classList.add('is-initial-selected');
        }

        this.setImageSetMedia();

        if (thumbnails) {
          let activeThumb = thumbnails.querySelector(thumbId);

          if (activeThumb) {
            activeThumb.classList.add('is-active');
            activeThumb.classList.add('is-initial-selected');
          }
        }

        productSlider.reInit(this.imageSetIndex);
        productSlider.selectCell(mediaId, this.preventScroll);

      } else if (flkty) {
        productSlider.selectCell(mediaId, this.preventScroll);
      }

    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateShareUrl() {
      const shareButton = document.getElementById(`Share-${this.dataset.section}`);
      if (!shareButton) return;
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      });
    }

    clearVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (input) input.value = '';
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('.pickup-availability-wrapper');

      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available && pickUpAvailability.fetchAvailability) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;

      const productForm = section.querySelector('product-form');
      if (productForm) productForm.handleErrorMessage();
    }

    getSectionsToRender() {
      return [`price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`, `product-image-${this.dataset.section}--sticky`, `inventory-${this.dataset.section}`, `sku-${this.dataset.section}`, `quantity-${this.dataset.section}`];
    }

    renderProductInfo() {
      const sections = this.getSectionsToRender();

      // Use AbortController signal to cancel stale requests
      fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`, {
        signal: this.abortController?.signal
      })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          sections.forEach((id) => {
            const destination = document.getElementById(id);
            const source = html.getElementById(id);

            if (source && destination) destination.innerHTML = source.innerHTML;

            const price = document.getElementById(id);
            const priceFixed = document.getElementById(id + '--sticky');

            if (price) price.classList.remove('visibility-hidden');
            if (priceFixed) priceFixed.classList.remove('visibility-hidden');
          });
          // Button state already set synchronously - no need to update here
        })
        .catch((error) => {
          // Ignore abort errors (expected when user changes selection quickly)
          if (error.name === 'AbortError') return;
          console.error('Error fetching product info:', error);
        });
    }

    /**
     * Unified Button State Management
     * States: 'updating' | 'ready' | 'sold-out' | 'choose'
     *
     * Simplified state machine (per 3-agent review):
     * - 'choose': No size selected - "Select options" text, enabled, validates on submit
     * - 'ready': Valid selection available - "Add to Cart" text, enabled
     * - 'sold-out': Selection out of stock - "Sold Out" text, disabled
     * - 'updating': Transition state - shows loading indicator
     */
    setButtonState(state) {
      const buttons = this.submitButtons || document.querySelectorAll('.single-add-to-cart-button');
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      const productTemplate = productForm?.closest('.product-form')?.getAttribute('template');

      // Price elements - hide when sold out/unavailable
      const price = document.getElementById(`price-${this.dataset.section}`);
      const priceFixed = document.getElementById(`price-${this.dataset.section}--sticky`);

      buttons.forEach((button) => {
        const buttonText = button.querySelector('.single-add-to-cart-button--text');

        // Reset ALL state classes
        button.classList.remove('is-updating', 'is-unavailable');

        switch (state) {
          case 'updating':
            button.classList.add('is-updating');
            break;

          case 'choose':
            // No size selected: button enabled, validates on submit
            button.removeAttribute('disabled');
            if (buttonText) {
              buttonText.dataset.content = window.theme.variantStrings.selectOptions || 'Select Size';
            }
            break;

          case 'ready':
            button.removeAttribute('disabled');
            if (buttonText) {
              buttonText.dataset.content = productTemplate?.includes('pre-order')
                ? window.theme.variantStrings.preOrder
                : window.theme.variantStrings.addToCart;
            }
            break;

          case 'sold-out':
            button.classList.add('is-unavailable');
            button.setAttribute('disabled', 'disabled');
            if (buttonText) {
              buttonText.dataset.content = window.theme.variantStrings.soldOut;
            }
            break;
        }
      });

      // Handle price visibility: hide when sold out
      if (state === 'sold-out') {
        price?.classList.add('visibility-hidden');
        priceFixed?.classList.add('visibility-hidden');
      } else if (state === 'ready' || state === 'choose') {
        price?.classList.remove('visibility-hidden');
        priceFixed?.classList.remove('visibility-hidden');
      }
    }

    // Convenience methods for clarity
    setButtonUpdating() {
      this.setButtonState('updating');
    }

    setButtonReady() {
      this.setButtonState('ready');
    }

    setButtonSoldOut() {
      this.setButtonState('sold-out');
    }

    setButtonChoose() {
      this.setButtonState('choose');
    }

    setDisabled() {
      if (this.isDisabledFeature != 'true') {
        return;
      }
      const variant_data = this.getVariantData();

      if (variant_data && this.currentVariant) {
        // Standard behavior: show availability based on selected options
        const selected_options = this.currentVariant.options.map((value, index) => {
          return {
            value,
            index: `option${index + 1}`
          };
        });
        const available_options = this.createAvailableOptionsTree(variant_data, selected_options);

        this.fieldsets.forEach((fieldset, i) => {
          const fieldset_options = Object.values(available_options)[i];

          if (fieldset_options) {
            if (fieldset.querySelector('select')) {
              fieldset_options.forEach((option, option_i) => {
                if (option.isUnavailable) {
                  fieldset.querySelector('option[value=' + JSON.stringify(option.value) + ']').disabled = true;
                } else {
                  fieldset.querySelector('option[value=' + JSON.stringify(option.value) + ']').disabled = false;
                }
              });
            } else if (fieldset.querySelectorAll('input').length) {
              fieldset.querySelectorAll('input').forEach((input, input_i) => {
                input.classList.toggle('is-disabled', fieldset_options[input_i].isUnavailable);
              });
            }
          }
        });

      } else if (variant_data && !this.currentVariant) {
        // No variant selected: show initial availability based on whether ANY variant with that option is available
        this.setInitialAvailability(variant_data);
      }
      return true;
    }

    /**
     * Set initial availability styling when no variant is selected.
     * An option is available if at least one variant with that option value is in stock.
     */
    setInitialAvailability(variant_data) {
      this.fieldsets.forEach((fieldset, fieldsetIndex) => {
        const optionKey = `option${fieldsetIndex + 1}`;

        if (fieldset.querySelector('select')) {
          fieldset.querySelectorAll('option').forEach(optionEl => {
            if (!optionEl.value) return; // Skip placeholder options
            const hasAvailableVariant = variant_data.some(variant =>
              variant[optionKey] === optionEl.value && variant.available
            );
            optionEl.disabled = !hasAvailableVariant;
          });
        } else if (fieldset.querySelectorAll('input').length) {
          fieldset.querySelectorAll('input').forEach(input => {
            const hasAvailableVariant = variant_data.some(variant =>
              variant[optionKey] === input.value && variant.available
            );
            input.classList.toggle('is-disabled', !hasAvailableVariant);
          });
        }
      });
    }

    getImageSetName(variant_name) {
      return variant_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
    }

    setImageSet() {
      if (!this.productSlider) return;

      let dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        this.imageSetIndex = this.querySelector('.product-form__input[data-handle="' + this.imageSetName + '"]').dataset.index;
        this.dataset.imageSetIndex = this.imageSetIndex;

        this.setImageSetMedia();
      }
    }
    setImageSetMedia() {
      if (!this.imageSetIndex || !this.currentVariant) {
        return;
      }

      let setValue = this.getImageSetName(this.currentVariant[this.imageSetIndex]);
      let group = this.imageSetName + '_' + setValue;
      let selected_set_images = this.productWrapper.querySelectorAll(`.product-images__slide[data-set-name="${this.imageSetName}"]`),
        selected_set_thumbs = this.productWrapper.querySelectorAll(`.product-thumbnail[data-set-name="${this.imageSetName}"]`);

      if (this.hideVariants) {
        // Product images
        this.productWrapper.querySelectorAll('.product-images__slide').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_images.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });

        // Product thumbnails
        this.productWrapper.querySelectorAll('.product-thumbnail').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_thumbs.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });
      }

    }

    createAvailableOptionsTree(variant_data, selected_options) {
      // Reduce variant array into option availability tree
      return variant_data.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = {
              value: variant[index],
              isUnavailable: true
            };
            options[index].push(entry);
          }

          // Check how many selected option values match a variant
          const countVariantOptionsThatMatchCurrent = selected_options.reduce((count, {
            value,
            index
          }) => {
            return variant[index] === value ? count + 1 : count;
          }, 0);

          // Only enable an option if an available variant matches all but one current selected value
          if (countVariantOptionsThatMatchCurrent >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          // Make sure if a variant is unavailable, disable currently selected option
          if ((!this.currentVariant || !this.currentVariant.available) && selected_options.find((option) => option.value === entry.value && index === option.index)) {
            entry.isUnavailable = true;
          }

          // First option is always enabled
          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /**
   *  @class
   *  @function VariantRadios
   */
  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }

    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.allOptionsSelected = true;

      this.options = fieldsets.map((fieldset) => {
        const checkedRadio = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
        if (checkedRadio) {
          // Clear error styling when option is selected
          fieldset.classList.remove('has-error');
          const errorMsg = fieldset.querySelector('.variant-error');
          if (errorMsg) errorMsg.remove();
          return checkedRadio.value;
        } else {
          this.allOptionsSelected = false;
          return null;
        }
      });
    }
  }

  customElements.define('variant-radios', VariantRadios);
}

if (!customElements.get('product-slider')) {
  /**
   *  @class
   *  @function ProductSlider
   */
  class ProductSlider extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', this.setupProductGallery);
    }
    connectedCallback() {
      this.product_container = this.closest('.thb-product-detail');
      this.quickView = this.closest('#Product-Drawer-Content');
      this.thumbnail_container = this.product_container.querySelector('.product-thumbnail-container');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');
      this.progress_bar = this.product_container.querySelector('.flickity-progress--bar'),

        this.setOptions();
      // Start Slider
      this.init();
    }
    setOptions() {
      this.hide_variants = this.dataset.hideVariants == 'true';
      this.thumbnails = this.thumbnail_container?.querySelectorAll('.product-thumbnail');
      this.prev_button = this.querySelector('.flickity-prev');
      this.next_button = this.querySelector('.flickity-next');
      this.images = this.querySelectorAll('img');
      this.options = {
        wrapAround: true,
        pageDots: true,
        contain: true,
        cellAlign: this.quickView ? 'left' : 'right',
        adaptiveHeight: !this.quickView,
        initialIndex: '.is-initial-selected',
        prevNextButtons: false,
        rightToLeft: document.dir === 'rtl',
        fade: false,
        cellSelector: '.product-images__slide.is-active',
        on: {
          ready: () => {
            this.images.forEach((img) => {
              img.addEventListener('lazyloaded', () => {
                this.flkty.resize();
              });
            });
          },
          dragStart: () => {
            this.flkty.slider.childNodes.forEach(slide => slide.style.pointerEvents = "none");
          },
          dragEnd: () => {
            this.flkty.slider.childNodes.forEach(slide => slide.style.pointerEvents = "all");
          }
        }
      };
      if (this.quickView) {
        this.options.wrapAround = false;
      }
      if (this.progress_bar) {
        this.options.on.scroll = (progress) => {
          progress = Math.max(0, Math.min(1, progress));
          this.progress_bar.style.width = progress * 100 + '%';
        };
      }
    }
    init() {
      this.flkty = new Flickity(this, this.options);

      this.selectedIndex = this.flkty.selectedIndex;

      // Setup Events
      this.setupEvents();

      // Start Gallery
      this.setupProductGallery();

      // Auto-expand thumbnails on page load
      this.autoExpandThumbnails();
    }
    reInit(imageSetIndex) {
      this.flkty.destroy();

      this.setOptions();

      this.flkty = new Flickity(this, this.options);

      // Setup Events
      this.setupEvents();

      this.selectedIndex = this.flkty.selectedIndex;
    }
    setupEvents() {
      const _this = this;
      if (this.prev_button) {
        let prev = this.prev_button.cloneNode(true);
        this.prev_button.parentNode.append(prev);
        this.prev_button.remove();
        prev.addEventListener('click', (event) => {
          document.dir === 'rtl' ? this.flkty.next() : this.flkty.previous();
        });
        prev.addEventListener('keyup', (event) => {
          document.dir === 'rtl' ? this.flkty.next() : this.flkty.previous();
          event.preventDefault();
        });
      }
      if (this.next_button) {
        let next = this.next_button.cloneNode(true);
        this.next_button.parentNode.append(next);
        this.next_button.remove();
        next.addEventListener('click', (event) => {
          document.dir === 'rtl' ? this.flkty.previous() : this.flkty.next();
        });
        next.addEventListener('keyup', (event) => {
          document.dir === 'rtl' ? this.flkty.previous() : this.flkty.next();
          event.preventDefault();
        });
      }
      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', function () {
          container.setAttribute('hidden', '');
        });
      });
      this.flkty.on('settle', function (index) {
        _this.selectedIndex = index;
      });
      this.flkty.on('change', (index) => {

        let previous_slide = this.flkty.cells[_this.selectedIndex].element,
          previous_media = previous_slide.querySelector('.product-single__media');

        if (this.thumbnails) {
          let active_thumbs = Array.from(this.thumbnails).filter(element => element.classList.contains('is-active')),
            active_thumb = active_thumbs[index] ? active_thumbs[index] : active_thumbs[0];

          this.thumbnails.forEach((item, i) => {
            item.classList.remove('is-initial-selected');
          });
          active_thumb.classList.add('is-initial-selected');

          requestAnimationFrame(() => {
            if (active_thumb.offsetParent === null) {
              return;
            }
            const windowHalfHeight = active_thumb.offsetParent.clientHeight / 2,
              windowHalfWidth = active_thumb.offsetParent.clientWidth / 2;
            active_thumb.parentElement.scrollTo({
              left: active_thumb.offsetLeft - windowHalfWidth + active_thumb.clientWidth / 2,
              top: active_thumb.offsetTop - windowHalfHeight + active_thumb.clientHeight / 2,
              behavior: 'smooth'
            });
          });
        }


        // Stop previous video
        if (previous_media.classList.contains('product-single__media-external-video')) {
          if (previous_media.dataset.provider === 'youtube') {
            previous_media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: ""
            }), "*");
          } else if (previous_media.dataset.provider === 'vimeo') {
            previous_media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
              method: "pause"
            }), "*");
          }
          previous_media.querySelector('.product-single__media-external-video--play').removeAttribute('hidden');
        } else if (previous_media.classList.contains('product-single__media-native-video')) {
          previous_media.querySelector('video').pause();
        }

      });

      if (this.thumbnails && !this.thumbnail_container.dataset.init) {
        setTimeout(() => {
          let active_thumbs = Array.from(this.thumbnails).filter(element => element.clientWidth > 0);
          active_thumbs.forEach((thumbnail, index) => {
            thumbnail.addEventListener('click', () => {
              this.thumbnailClick(thumbnail, index);
            });
          });
          this.thumbnail_container.addEventListener('click', (e) => {
            let count = Math.min(Math.max(this.thumbnail_container.querySelectorAll('.is-active').length, 1), 4);
            this.thumbnail_container.style.setProperty('--thumbnail-width', (count * 63) + 19 + 'px');
            if (!this.thumbnail_container.classList.contains('extended')) {
              this.thumbnail_container.classList.add('extended');
            } else {
              if (e.target.nodeName == 'BUTTON') {
                this.thumbnail_container.classList.remove('extended');
              }
              e.preventDefault();
            }
            if (!this.thumbnail_container.classList.contains('extended')) {
              let active_thumb = this.thumbnail_container.querySelector('.is-initial-selected');
              if (active_thumb) {
                setTimeout(() => {
                  active_thumb.parentElement.scrollTo({
                    left: Math.max(active_thumb.offsetLeft, 0),
                    top: 0,
                    behavior: 'smooth'
                  });
                }, 300);
              }
            }
          });
          this.thumbnail_container.dataset.init = true;
        });
      }
    }
    thumbnailClick(thumbnail, index) {
      [].forEach.call(this.thumbnails, function (el) {
        el.classList.remove('is-initial-selected');
      });
      thumbnail.classList.add('is-initial-selected');
      this.flkty.select(index);
    }
    setDraggable(draggable) {
      this.flkty.options.draggable = draggable;
      this.flkty.updateDraggable();
    }
    selectCell(mediaId, isInstant) {
      this.flkty.selectCell(mediaId, false, isInstant);
    }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }
      this.setEventListeners();
    }
    autoExpandThumbnails() {
      if (this.thumbnail_container && !this.thumbnail_container.classList.contains('extended')) {
        // Calculate the width based on active thumbnails (same logic as the existing click handler)
        let count = Math.min(Math.max(this.thumbnail_container.querySelectorAll('.is-active').length, 1), 4);
        this.thumbnail_container.style.setProperty('--thumbnail-width', (count * 63) + 19 + 'px');
        // Add the extended class to show thumbnails expanded
        this.thumbnail_container.classList.add('extended');
      }
    }
    buildItems() {
      this.activeImages = Array.from(this.querySelectorAll('.product-images__slide.is-active .product-single__media-image'));

      return this.activeImages.map((item) => {
        let index = [].indexOf.call(item.parentNode.parentNode.children, item.parentNode);

        let activelink = item.querySelector('.product-single__media-zoom');

        activelink.dataset.index = index;
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h
        };
      });
    }
    setEventListeners() {
      this.links = this.querySelectorAll('.product-single__media-zoom');
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: false,
        allowPanToNext: false,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true,
        getThumbBoundsFn: () => {
          const thumbnail = this.querySelector('.product-images__slide.is-selected'),
            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        }
      };


      this.links.forEach((link => {
        link.addEventListener('click', (e) => this.zoomClick(e, link));
      }));
    }
    zoomClick(e, link) {
      this.items = this.buildItems();
      this.pswpOptions.index = parseInt(link.dataset.index, 10);
      if (typeof PhotoSwipe !== 'undefined') {
        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, this.items, this.pswpOptions);
        pswp.listen('firstUpdate', function () {
          pswp.listen('parseVerticalMargin', function (item) {
            item.vGap = {
              top: 50,
              bottom: 50
            };
          });
        });
        pswp.init();
      }
      e.preventDefault();
    }
  }
  customElements.define('product-slider', ProductSlider);
}

/**
 *  @class
 *  @function ProductForm
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.sticky = this.dataset.sticky;
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      if (!this.sticky) {
        // Prevent duplicate submit listeners by checking if one is already attached
        if (!this.form.dataset.submitListenerAttached) {
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
          this.form.dataset.submitListenerAttached = 'true';
        }
      }
      this.cartNotification = document.querySelector('cart-notification');
      this.body = document.body;

      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    onSubmitHandler(evt) {
      evt.preventDefault();

      if (!this.form.reportValidity()) {
        return;
      }

      // Check if variant is selected (for products that require size selection)
      const variantInput = this.form.querySelector('input[name="id"]');
      if (!variantInput.value) {
        this.showSizeError();
        return;
      }

      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');

      submitButtons.forEach((submitButton) => {
        if (submitButton.classList.contains('loading')) return;
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
      });

      this.handleErrorMessage();


      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };


      let formData = new FormData(this.form);

      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });
            if (response.status === 422) {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true
              }));
            }
            this.handleErrorMessage(response.description);
            return;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
          if (document.querySelector('.product-add-to-cart-sticky-modal')) {
            document.querySelector('.product-add-to-cart-sticky-modal').removeAttribute('open');
          }
        });
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (section.id == 'Cart-Drawer') {
          // Event delegation handles remove buttons - no need to re-register
          elementToReplace.notesToggle();
        }
      }));

      // Only create one Cart instance, outside the loop
      if (typeof Cart !== 'undefined' && document.getElementById('Cart')) {
        new Cart().renderContents(parsedState);
      }



      let product_drawer = document.getElementById('Product-Drawer'),
        search_drawer = document.getElementById('Search-Drawer');

      if (search_drawer && search_drawer.classList.contains('active')) {
        search_drawer.classList.remove('active');
      }
      if (product_drawer && product_drawer.contains(this)) {
        product_drawer.classList.remove('active');
        this.body.classList.remove('open-cc--product');
        if (document.getElementById('Cart-Drawer')) {
          this.body.classList.add('open-cc');
          document.getElementById('Cart-Drawer').classList.add('active');
        }
      } else if (document.getElementById('Cart-Drawer')) {
        this.body.classList.add('open-cc');
        document.getElementById('Cart-Drawer').classList.add('active');
        dispatchCustomEvent('cart-drawer:open');
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    showSizeError() {
      // Find the Size fieldset (marked with data-required or data-handle="size")
      const sizeFieldset = document.querySelector('fieldset[data-handle="size"]') ||
                           document.querySelector('fieldset[data-required="true"]');

      if (!sizeFieldset) {
        // Fallback: show general error
        this.handleErrorMessage(window.theme?.variantStrings?.selectSize || 'Please select a size');
        return;
      }

      // Add error styling
      sizeFieldset.classList.add('has-error');

      // Show error message if not already present
      let errorMsg = sizeFieldset.querySelector('.variant-error');
      if (!errorMsg) {
        errorMsg = document.createElement('div');
        errorMsg.className = 'variant-error';
        errorMsg.setAttribute('role', 'alert');
        errorMsg.setAttribute('aria-live', 'assertive');
        errorMsg.textContent = window.theme?.variantStrings?.selectSize || 'Please select a size';
        sizeFieldset.appendChild(errorMsg);
      }

      // Scroll to fieldset
      sizeFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Focus first radio button for accessibility (preventScroll avoids page jump)
      const firstInput = sizeFieldset.querySelector('input[type="radio"]');
      if (firstInput) {
        setTimeout(() => firstInput.focus({ preventScroll: true }), 300);
      }
    }
  });
}

/**
 *  @class
 *  @function ProductAddToCartSticky
 */
if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.setupObservers();
      this.drawer = document.getElementById(this.dataset.target);
      this.addEventListener('click', this.onClick.bind(this));
    }
    onClick() {
      this.drawer.show();
    }
    setupObservers() {
      let _this = this,
        observer = new IntersectionObserver(function (entries) {
          entries.forEach((entry) => {
            if (entry.target === footer) {
              if (entry.intersectionRatio > 0) {
                _this.classList.remove('sticky--visible');
              } else if (entry.intersectionRatio == 0 && _this.formPassed) {
                _this.classList.add('sticky--visible');
              }
            }
            if (entry.target === form) {
              let boundingRect = form.getBoundingClientRect();

              if (entry.intersectionRatio === 0 && window.scrollY > (boundingRect.top + boundingRect.height)) {
                _this.formPassed = true;
                _this.classList.add('sticky--visible');
              } else if (entry.intersectionRatio === 1) {
                _this.formPassed = false;
                _this.classList.remove('sticky--visible');
              }
            }
          });
        }, {
          threshold: [0, 1]
        }),
        form = document.getElementById(`product-form-${this.dataset.section}`),
        footer = document.getElementById('footer');
      _this.formPassed = false;
      observer.observe(form);
      if (footer) observer.observe(footer);
    }
  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}

if (typeof addIdToRecentlyViewed !== "undefined") {
  addIdToRecentlyViewed();
}